import React, { useEffect, useState, useCallback } from 'react';
import { useTheme } from '../../../assets/contexts/ThemeContext';
import CustomSelect from '../../../components/widgets/CustomSelect';

/**
 * 헤더 로고 제어 컴포넌트
 * 로고 타입(이미지/태그)을 선택하고 설정할 수 있습니다.
 */
const HeaderLogoControl = () => {
  const themeContext = useTheme();
  const { themeConfig, set } = themeContext || {};

  // 기본값
  const defaultLogoType = 'image';
  const defaultImage = '/assets/images/logo/logo.png';
  // HeaderAreaManager.jsx 구조와 호환 (title/subtitle은 { kor, eng } 또는 문자열)
  const defaultTag = {
    logo: {
      size: 42,
      backgroundColor: 'rgb(147, 51, 234)',
      letter: 'G',
      fontSize: '1.25rem',
      color: '#ffffff'
    },
    title: { kor: 'GLOW Platform', eng: 'GLOW Platform' },
    titleColor: '#9333ea',
    titleStyle: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '0',
      textTransform: 'none'
    },
    subtitle: { kor: '지산학 R&D 플랫폼', eng: 'Industry-Academia R&D Platform' },
    subtitleColor: '#717182',
    subtitleStyle: {
      fontSize: '0.65rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0',
      textTransform: 'none'
    }
  };
  const titleToDisplay = (v) => (typeof v === 'object' && v && ('kor' in v || 'eng' in v) ? (v.kor ?? v.eng ?? '') : (v ?? ''));
  const subtitleToDisplay = (v) => (typeof v === 'object' && v && ('kor' in v || 'eng' in v) ? (v.kor ?? v.eng ?? '') : (v ?? ''));

  // State
  const [logoType, setLogoType] = useState(defaultLogoType);
  const [imagePath, setImagePath] = useState(defaultImage);
  const [tagConfig, setTagConfig] = useState(defaultTag);

  // 초기화
  useEffect(() => {
    if (themeConfig?.headerLogo) {
      const headerLogo = themeConfig.headerLogo;

      if (headerLogo.type) {
        setLogoType(headerLogo.type);
      }

      if (headerLogo.image) {
        setImagePath(headerLogo.image);
      }

      if (headerLogo.tag) {
        const t = headerLogo.tag;
        const titleVal = t.title ?? defaultTag.title;
        const subtitleVal = t.subtitle ?? defaultTag.subtitle;
        setTagConfig({
          logo: {
            size: t.logo?.size ?? defaultTag.logo.size,
            backgroundColor: t.logo?.backgroundColor ?? defaultTag.logo.backgroundColor,
            letter: t.logo?.letter ?? defaultTag.logo.letter,
            fontSize: t.logo?.fontSize ?? defaultTag.logo.fontSize,
            color: t.logo?.color ?? defaultTag.logo.color
          },
          title: typeof titleVal === 'object' && titleVal ? { kor: titleVal.kor ?? '', eng: titleVal.eng ?? '' } : { kor: titleVal ?? '', eng: titleVal ?? '' },
          titleColor: t.titleColor ?? defaultTag.titleColor,
          titleStyle: {
            fontSize: t.titleStyle?.fontSize ?? defaultTag.titleStyle.fontSize,
            fontWeight: t.titleStyle?.fontWeight ?? defaultTag.titleStyle.fontWeight,
            lineHeight: t.titleStyle?.lineHeight ?? defaultTag.titleStyle.lineHeight,
            letterSpacing: t.titleStyle?.letterSpacing ?? defaultTag.titleStyle.letterSpacing,
            textTransform: t.titleStyle?.textTransform ?? defaultTag.titleStyle.textTransform
          },
          subtitle: typeof subtitleVal === 'object' && subtitleVal ? { kor: subtitleVal.kor ?? '', eng: subtitleVal.eng ?? '' } : { kor: subtitleVal ?? '', eng: subtitleVal ?? '' },
          subtitleColor: t.subtitleColor ?? defaultTag.subtitleColor,
          subtitleStyle: {
            fontSize: t.subtitleStyle?.fontSize ?? defaultTag.subtitleStyle.fontSize,
            fontWeight: t.subtitleStyle?.fontWeight ?? defaultTag.subtitleStyle.fontWeight,
            lineHeight: t.subtitleStyle?.lineHeight ?? defaultTag.subtitleStyle.lineHeight,
            letterSpacing: t.subtitleStyle?.letterSpacing ?? defaultTag.subtitleStyle.letterSpacing,
            textTransform: t.subtitleStyle?.textTransform ?? defaultTag.subtitleStyle.textTransform
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeConfig]);

  // 로고 타입 변경
  const handleLogoTypeChange = useCallback((e) => {
    const newType = e.target.value;
    setLogoType(newType);

    if (set) {
      const currentLogo = themeConfig?.headerLogo || {};
      const updatedLogo = {
        ...currentLogo,
        type: newType
      };

      // 태그 타입으로 변경할 때 tag 속성이 없으면 기본값 추가 (title/subtitle은 { kor, eng } 유지)
      if (newType === 'tag' && !updatedLogo.tag) {
        updatedLogo.tag = {
          logo: {
            size: tagConfig.logo.size,
            backgroundColor: tagConfig.logo.backgroundColor,
            letter: tagConfig.logo.letter,
            fontSize: tagConfig.logo.fontSize,
            color: tagConfig.logo.color
          },
          title: typeof tagConfig.title === 'object' && tagConfig.title ? tagConfig.title : { kor: tagConfig.title ?? '', eng: tagConfig.title ?? '' },
          titleColor: tagConfig.titleColor,
          titleStyle: tagConfig.titleStyle,
          subtitle: typeof tagConfig.subtitle === 'object' && tagConfig.subtitle ? tagConfig.subtitle : { kor: tagConfig.subtitle ?? '', eng: tagConfig.subtitle ?? '' },
          subtitleColor: tagConfig.subtitleColor,
          subtitleStyle: tagConfig.subtitleStyle
        };
      }

      set('headerLogo', updatedLogo);
    }
  }, [set, themeConfig, tagConfig]);

  // 이미지 경로 변경
  const handleImagePathChange = useCallback((e) => {
    const newPath = e.target.value;
    setImagePath(newPath);

    if (set) {
      const currentLogo = themeConfig?.headerLogo || {};
      set('headerLogo', {
        ...currentLogo,
        type: 'image',
        image: newPath
      });
    }
  }, [set, themeConfig]);

  // 태그 설정 변경
  const handleTagConfigChange = useCallback((field, value) => {
    const newTagConfig = { ...tagConfig };

    if (field.startsWith('logo.')) {
      const logoField = field.replace('logo.', '');
      newTagConfig.logo = {
        ...newTagConfig.logo,
        [logoField]: value
      };
    } else if (field.startsWith('titleStyle.')) {
      const styleField = field.replace('titleStyle.', '');
      newTagConfig.titleStyle = {
        ...newTagConfig.titleStyle,
        [styleField]: value
      };
    } else if (field.startsWith('subtitleStyle.')) {
      const styleField = field.replace('subtitleStyle.', '');
      newTagConfig.subtitleStyle = {
        ...newTagConfig.subtitleStyle,
        [styleField]: value
      };
    } else if (field === 'title') {
      // 제목 편집 시 { kor, eng } 유지 (한글 입력값을 kor/eng 둘 다에 반영)
      const str = typeof value === 'string' ? value : '';
      newTagConfig.title = { kor: str, eng: newTagConfig.title?.eng ?? str };
    } else if (field === 'subtitle') {
      const str = typeof value === 'string' ? value : '';
      newTagConfig.subtitle = { kor: str, eng: newTagConfig.subtitle?.eng ?? str };
    } else {
      newTagConfig[field] = value;
    }

    setTagConfig(newTagConfig);

    if (set) {
      const currentLogo = themeConfig?.headerLogo || {};
      set('headerLogo', {
        ...currentLogo,
        type: 'tag',
        tag: newTagConfig
      });
    }
  }, [tagConfig, set, themeConfig]);

  if (!themeContext) return null;

  return (
    <>
      <style>{`
        #header-logo-control-section:not(:last-child) {
          padding-bottom: 15px;
        }
        .header-logo-control-header {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 12px;
        }
        .header-logo-control-header > label {
          display: block;
          font-weight: bold;
          color: #333;
          font-size: 13px;
          cursor: default;
          margin: 0;
          flex: 1;
        }
        #header-logo-control-section .theme-help-btn {
          background: transparent;
          border: 1px solid #ddd;
          color: #666;
          font-size: 11px;
          width: 20px;
          height: 20px;
          padding: 0;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }
        .header-logo-control-description {
          font-size: 11px;
          color: #999;
          margin-bottom: 12px;
          line-height: 1.4;
        }
        .header-logo-control-field {
          margin-bottom: 18px;
        }
        .header-logo-control-field-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 18px;
        }
        .header-logo-control-field-grid .header-logo-control-field {
          margin-bottom: 0;
        }
        .header-logo-control-field-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 6px;
        }
        .header-logo-control-field-header > label {
          font-size: 12px;
          color: #666;
          font-weight: 500;
          cursor: pointer;
        }
        .header-logo-control-input {
          width: 100%;
          padding: 6px 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 12px;
        }
        .header-logo-control-select {
          width: 100%;
        }
        .header-logo-control-select .header-logo-control-select__control {
          min-height: 34px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 12px;
          background-color: #fff;
        }
        .header-logo-control-select .header-logo-control-select__value-container {
          padding: 0 10px;
        }
        .header-logo-control-select .header-logo-control-select__indicators {
          height: 34px;
        }
        .header-logo-control-number-input {
          width: 100%;
          padding: 6px 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 12px;
          text-align: center;
        }
        .header-logo-control-field-description {
          font-size: 10px;
          color: #999;
          margin-top: 4px;
          line-height: 1.4;
        }
        .header-logo-control-tag-section {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #eee;
        }
        .header-logo-control-tag-section-title {
          font-size: 12px;
          font-weight: 600;
          color: #333;
          margin-bottom: 12px;
        }
      `}</style>
      <div id="header-logo-control-section">
        <div className="header-logo-control-header">
          <label>🎨 헤더 로고 설정:</label>
          <button
            type="button"
            className="theme-help-btn"
            data-target="header-logo-control-section"
            title="이 항목으로 이동"
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f5f5f5';
              e.target.style.borderColor = 'var(--theme-color-primary)';
              e.target.style.color = 'var(--theme-color-primary)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = '#ddd';
              e.target.style.color = '#666';
            }}
          >
            ?
          </button>
        </div>
        <div className="header-logo-control-description">
          헤더 영역의 로고를 이미지 또는 태그 방식으로 설정할 수 있습니다.
        </div>

        {/* 로고 타입 선택 */}
        <div className="header-logo-control-field">
          <div className="header-logo-control-field-header">
            <label htmlFor="header-logo-type-select">로고 타입:</label>
          </div>
          <CustomSelect
            inputId="header-logo-type-select"
            className="header-logo-control-select"
            classNamePrefix="header-logo-control-select"
            value={logoType}
            onChange={(v) => handleLogoTypeChange({ target: { value: v } })}
            isSearchable={false}
            isClearable={false}
            options={[
              { value: 'image', label: '이미지' },
              { value: 'tag', label: '태그' },
            ]}
          />
          <div className="header-logo-control-field-description">
            이미지: 기존 이미지 파일을 사용합니다. 태그: HTML 태그로 구성된 로고를 사용합니다.
          </div>
        </div>

        {/* 이미지 타입 설정 */}
        {logoType === 'image' && (
          <div className="header-logo-control-field">
            <div className="header-logo-control-field-header">
              <label htmlFor="header-logo-image-input">이미지 경로:</label>
            </div>
            <input
              type="text"
              id="header-logo-image-input"
              className="header-logo-control-input"
              value={imagePath}
              onChange={handleImagePathChange}
              placeholder="/assets/images/logo/logo.png"
            />
            <div className="header-logo-control-field-description">
              로고 이미지 파일의 경로를 입력하세요. (예: /assets/images/logo/logo.png)
            </div>
          </div>
        )}

        {/* 태그 타입 설정 */}
        {logoType === 'tag' && (
          <div className="header-logo-control-tag-section">
            <div className="header-logo-control-tag-section-title">태그 로고 설정</div>

            {/* 원형 로고 설정 */}
            <div className="header-logo-control-field-grid">
              <div className="header-logo-control-field">
                <div className="header-logo-control-field-header">
                  <label htmlFor="header-logo-tag-size-input">로고 크기 (px):</label>
                </div>
                <input
                  type="number"
                  id="header-logo-tag-size-input"
                  className="header-logo-control-number-input"
                  value={tagConfig.logo.size}
                  onChange={(e) => handleTagConfigChange('logo.size', parseInt(e.target.value, 10) || 49)}
                  min="20"
                  max="100"
                />
              </div>

              <div className="header-logo-control-field">
                <div className="header-logo-control-field-header">
                  <label htmlFor="header-logo-tag-letter-input">로고 글자:</label>
                </div>
                <input
                  type="text"
                  id="header-logo-tag-letter-input"
                  className="header-logo-control-input"
                  value={tagConfig.logo.letter}
                  onChange={(e) => handleTagConfigChange('logo.letter', e.target.value)}
                  placeholder="G"
                  maxLength="1"
                />
              </div>
            </div>

            <div className="header-logo-control-field-grid">
              <div className="header-logo-control-field">
                <div className="header-logo-control-field-header">
                  <label htmlFor="header-logo-tag-fontsize-input">로고 글자 크기:</label>
                </div>
                <input
                  type="text"
                  id="header-logo-tag-fontsize-input"
                  className="header-logo-control-input"
                  value={tagConfig.logo.fontSize}
                  onChange={(e) => handleTagConfigChange('logo.fontSize', e.target.value)}
                  placeholder="1.5rem"
                />
                <div className="header-logo-control-field-description">
                  CSS 크기 단위로 입력하세요.
                </div>
              </div>

              <div className="header-logo-control-field">
                <div className="header-logo-control-field-header">
                  <label htmlFor="header-logo-tag-color-input">로고 글자 색상:</label>
                </div>
                <input
                  type="color"
                  id="header-logo-tag-color-input"
                  className="header-logo-control-input"
                  style={{ height: '40px' }}
                  value={tagConfig.logo.color}
                  onChange={(e) => handleTagConfigChange('logo.color', e.target.value)}
                />
              </div>
            </div>

            <div className="header-logo-control-field">
              <div className="header-logo-control-field-header">
                <label htmlFor="header-logo-tag-bg-input">로고 배경 (그라데이션):</label>
              </div>
              <input
                type="text"
                id="header-logo-tag-bg-input"
                className="header-logo-control-input"
                value={tagConfig.logo.backgroundColor}
                onChange={(e) => handleTagConfigChange('logo.backgroundColor', e.target.value)}
                placeholder="linear-gradient(135deg, #9333ea 0%, #3b82f6 100%)"
              />
              <div className="header-logo-control-field-description">
                CSS 그라데이션 형식으로 입력하세요. (예: linear-gradient(135deg, #9333ea 0%, #3b82f6 100%))
              </div>
            </div>

            {/* 제목 설정 */}
            <div className="header-logo-control-field">
              <div className="header-logo-control-field-header">
                <label htmlFor="header-logo-tag-title-input">제목 텍스트:</label>
              </div>
              <input
                type="text"
                id="header-logo-tag-title-input"
                className="header-logo-control-input"
                value={titleToDisplay(tagConfig.title)}
                onChange={(e) => handleTagConfigChange('title', e.target.value)}
                placeholder="GLOW Platform"
              />
            </div>

            <div className="header-logo-control-field">
              <div className="header-logo-control-field-header">
                <label htmlFor="header-logo-tag-title-color-input">제목 색상:</label>
              </div>
              <input
                type="color"
                id="header-logo-tag-title-color-input"
                className="header-logo-control-input"
                style={{ height: '40px' }}
                value={tagConfig.titleColor}
                onChange={(e) => handleTagConfigChange('titleColor', e.target.value)}
              />
            </div>

            {/* 부제목 설정 */}
            <div className="header-logo-control-field">
              <div className="header-logo-control-field-header">
                <label htmlFor="header-logo-tag-subtitle-input">부제목 텍스트:</label>
              </div>
              <input
                type="text"
                id="header-logo-tag-subtitle-input"
                className="header-logo-control-input"
                value={subtitleToDisplay(tagConfig.subtitle)}
                onChange={(e) => handleTagConfigChange('subtitle', e.target.value)}
                placeholder="지산학 R&D 플랫폼"
              />
            </div>

            <div className="header-logo-control-field-grid">
              <div className="header-logo-control-field">
                <div className="header-logo-control-field-header">
                  <label htmlFor="header-logo-tag-subtitle-color-input">부제목 색상:</label>
                </div>
                <input
                  type="color"
                  id="header-logo-tag-subtitle-color-input"
                  className="header-logo-control-input"
                  style={{ height: '40px' }}
                  value={tagConfig.subtitleColor}
                  onChange={(e) => handleTagConfigChange('subtitleColor', e.target.value)}
                />
              </div>

              <div className="header-logo-control-field">
                <div className="header-logo-control-field-header">
                  <label htmlFor="header-logo-tag-subtitle-fontsize-input">부제목 폰트 크기:</label>
                </div>
                <input
                  type="text"
                  id="header-logo-tag-subtitle-fontsize-input"
                  className="header-logo-control-input"
                  value={tagConfig.subtitleStyle.fontSize}
                  onChange={(e) => handleTagConfigChange('subtitleStyle.fontSize', e.target.value)}
                  placeholder="0.65rem"
                />
              </div>
            </div>

            <div className="header-logo-control-field-grid">
              <div className="header-logo-control-field">
                <div className="header-logo-control-field-header">
                  <label htmlFor="header-logo-tag-subtitle-fontweight-input">부제목 폰트 굵기:</label>
                </div>
                <CustomSelect
                  inputId="header-logo-tag-subtitle-fontweight-input"
                  className="header-logo-control-select"
                  classNamePrefix="header-logo-control-select"
                  value={tagConfig.subtitleStyle.fontWeight}
                  onChange={(v) => handleTagConfigChange('subtitleStyle.fontWeight', Number(v))}
                  isSearchable={false}
                  isClearable={false}
                  options={[
                    { value: 100, label: '100 (Thin)' },
                    { value: 200, label: '200 (Extra Light)' },
                    { value: 300, label: '300 (Light)' },
                    { value: 400, label: '400 (Normal)' },
                    { value: 500, label: '500 (Medium)' },
                    { value: 600, label: '600 (Semi Bold)' },
                    { value: 700, label: '700 (Bold)' },
                    { value: 800, label: '800 (Extra Bold)' },
                    { value: 900, label: '900 (Black)' },
                  ]}
                />
              </div>

              <div className="header-logo-control-field">
                <div className="header-logo-control-field-header">
                  <label htmlFor="header-logo-tag-subtitle-lineheight-input">부제목 줄 간격:</label>
                </div>
                <input
                  type="text"
                  id="header-logo-tag-subtitle-lineheight-input"
                  className="header-logo-control-input"
                  value={tagConfig.subtitleStyle.lineHeight}
                  onChange={(e) => handleTagConfigChange('subtitleStyle.lineHeight', e.target.value)}
                  placeholder="1.5"
                />
              </div>
            </div>

            <div className="header-logo-control-field-grid">
              <div className="header-logo-control-field">
                <div className="header-logo-control-field-header">
                  <label htmlFor="header-logo-tag-subtitle-letterspacing-input">부제목 자간:</label>
                </div>
                <input
                  type="text"
                  id="header-logo-tag-subtitle-letterspacing-input"
                  className="header-logo-control-input"
                  value={tagConfig.subtitleStyle.letterSpacing}
                  onChange={(e) => handleTagConfigChange('subtitleStyle.letterSpacing', e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="header-logo-control-field">
                <div className="header-logo-control-field-header">
                  <label htmlFor="header-logo-tag-subtitle-texttransform-input">부제목 대소문자:</label>
                </div>
                <CustomSelect
                  inputId="header-logo-tag-subtitle-texttransform-input"
                  className="header-logo-control-select"
                  classNamePrefix="header-logo-control-select"
                  value={tagConfig.subtitleStyle.textTransform}
                  onChange={(v) => handleTagConfigChange('subtitleStyle.textTransform', v)}
                  isSearchable={false}
                  isClearable={false}
                  options={[
                    { value: 'none', label: '없음 (원본 유지)' },
                    { value: 'uppercase', label: '대문자' },
                    { value: 'lowercase', label: '소문자' },
                    { value: 'capitalize', label: '단어 첫 글자 대문자' },
                  ]}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HeaderLogoControl;
